import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import debounce from "lodash/debounce";
import { ReportTemplate } from "../../excel/reports/api";
import { discoverReportTemplates } from "../../excel/reports/api/registry";
import { ReportGroup } from "../../excel/reports/api/groups";
import { useDatasources } from "../context/DatasourceContext";
import { useReportData } from "../context/ReportDataContext";
import { useWorksheetListeners } from "../context/WorksheetListenerContext";
import { useReportActions } from "./useReportActions";
import { EdiphiReportTemplate } from "../../excel/reports/templates/registry";
import { handleError } from "../../errors";
import Logger from "../../clients/sentry";

/** The reports section a discovered report belongs to — declared on its registry entry. */
export const reportTypeOfTemplate = (template: ReportTemplate): ReportGroup => template.reportType.group;

/**
 * Reports already generated in the workbook, rediscovered whenever the workbook might have changed:
 * on estimate load, on sheet delete/rename, and after a report is created or removed.
 *
 * Only the first load for an estimate reports itself as loading. A rediscovery that replaces cards
 * already on screen stays silent, so a sheet deleted in Excel updates them in place instead of
 * swapping the whole section for skeletons.
 */
export function useReportCards() {
  const { ediphiDatasources } = useDatasources();
  const { refreshReportDataStatus, resetReportDataStatus } = useReportData();
  const { registerListeners } = useWorksheetListeners();
  const { getVisibleTemplates } = useReportActions();

  const [reports, setReports] = useState<ReportTemplate[]>([]);
  const [visibleTemplates, setVisibleTemplates] = useState<Record<string, EdiphiReportTemplate>>({});
  // Seeded from whether there is anything to discover, so the first paint is already right: with a
  // datasource the page shows skeletons instead of flashing its empty state, and without one it
  // shows the empty state instead of flashing skeletons.
  const [loadingReports, setLoadingReports] = useState(() => ediphiDatasources.length > 0);
  const isLoadingRef = useRef(false);
  const debouncedReloadRef = useRef<ReturnType<typeof debounce> | undefined>(undefined);
  // reloadReports is memoized with no deps so the handler handed to Excel stays stable, so it reads
  // what is currently loaded through a ref rather than a closed-over value.
  const hasDatasourceRef = useRef(ediphiDatasources.length > 0);
  hasDatasourceRef.current = ediphiDatasources.length > 0;
  // Re-runs when a datasource is added or removed, but not when they are merely reordered.
  const datasourceKey = useMemo(() => [...ediphiDatasources].sort().join("|"), [ediphiDatasources]);

  const reloadReports = useCallback(async (options?: { showLoading?: boolean }) => {
    // The sheet-event handler calls this regardless of what is loaded. With no datasource there is
    // nothing to discover, and letting it run would populate the sections from .metadata rows the
    // workbook still holds.
    if (!hasDatasourceRef.current) {
      setReports([]);
      setVisibleTemplates({});
      // Unconditional: this is what releases the loading state the hook seeds itself with.
      setLoadingReports(false);
      return;
    }

    isLoadingRef.current = true;
    if (options?.showLoading) setLoadingReports(true);
    try {
      const templates = await discoverReportTemplates();

      // Sections need this to know whether their Add button would open an empty drawer, so it is
      // computed here rather than when the drawer opens.
      const templatesByKey = await getVisibleTemplates();
      // Re-checked before every write below: the project header sits on this page, so a clear can land
      // while discovery is in flight, and writing then would paint the old cards back over the empty
      // state the clear just set.
      if (!hasDatasourceRef.current) return;
      setVisibleTemplates(templatesByKey);

      if (!templates.length) {
        setReports([]);
        resetReportDataStatus();
        return;
      }

      // Before publishing, so cards do not paint in their no-data state on the first render.
      await refreshReportDataStatus(templates);
      if (!hasDatasourceRef.current) return;
      setReports(templates);
    } catch (error) {
      const err = handleError(error, { context: "discoverReportTemplates" });
      Logger.error("Failed to discover report templates", err);
      throw err;
    } finally {
      isLoadingRef.current = false;
      if (options?.showLoading) setLoadingReports(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (ediphiDatasources.length === 0) {
      setReports([]);
      setVisibleTemplates({});
      // Nothing to discover, so release the initial loading state the page starts in.
      setLoadingReports(false);
      return;
    }

    const initialize = async () => {
      // Skeletons only when there is nothing to preserve. This runs on any datasource change, not just
      // the first, and swapping cards already on screen for skeletons is what rediscovery avoids.
      await reloadReports({ showLoading: reports.length === 0 });
      await registerListeners(() => debouncedReloadRef.current?.());
    };

    void initialize().catch(() => {
      // Logged in reloadReports; consumers surface the empty state.
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datasourceKey]);

  useEffect(() => {
    // Indirected through a ref so the handler handed to Excel stays stable across reloads.
    debouncedReloadRef.current = debounce(() => {
      if (!isLoadingRef.current) void reloadReports().catch(() => {});
    }, 100);

    return () => {
      debouncedReloadRef.current?.cancel?.();
    };
  }, [reloadReports]);

  return { reports, visibleTemplates, loadingReports, reloadReports };
}

export default useReportCards;
